import React, { useCallback, useEffect, useRef, useState } from "react";
import { Snackbar, SnackbarContent, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Context/AuthProvider";
import {
  usePortfolioRebalanceStore,
  buildRebalanceApiData,
} from "../../Store/portfolioRebalanceStore";
import { fetchRebalanceStatus } from "../../http/api";
import {
  readJob,
  writeJob,
  PENDING_JOB_KEY,
  JOB_STATUS,
  REBALANCE_OUTCOME,
  isPreviewableOutcome,
  isJobStale,
  isJobFresh,
  hasToastShownThisTab,
  markToastShownThisTab,
  classifyRebalanceResult,
  isTerminalRebalanceResult,
  resolveRebalanceResultWaiter,
  findJobByRequestId,
  listPendingJobs,
  listReadyUnseenJobs,
} from "../../Utils/portfolioRebalanceJobStorage";
import { tokens } from "../../Common/designTokens";

const POLL_INTERVAL_MS = 30000;

// The workbench route. When the user is here with the ready account selected, the
// inline "View Results" affordance already covers them → we skip the snackbar.
const WORKBENCH_PATH = "/portfolio-rebalance";

// Shown when a job is expired client-side because no result ever arrived.
const STALE_JOB_MESSAGE =
  "Your earlier rebalance took too long and may not have completed. Please try again.";

/**
 * Open a SPECIFIC account's READY result. Shared by the workbench header (fast
 * path + its inline affordance) and the smart opener below, so the "where do we
 * take the user" logic lives in one place.
 *
 * - TRADES → load the prebuilt preview into the store and open Order Preview.
 * - anything else → surface the info/error modal on the workbench.
 * Selects the account (so the preview submits against the right one) and marks
 * the job done (removed) once opened.
 */
export const useOpenRebalanceResult = () => {
  const navigate = useNavigate();
  const loadReadyResultIntoPreview = usePortfolioRebalanceStore(
    (s) => s.loadReadyResultIntoPreview
  );
  const restoreRebalanceContext = usePortfolioRebalanceStore(
    (s) => s.restoreRebalanceContext
  );
  const setPendingInfoModal = usePortfolioRebalanceStore((s) => s.setPendingInfoModal);
  const clearRebalanceJob = usePortfolioRebalanceStore((s) => s.clearRebalanceJob);

  return useCallback(
    (accountId) => {
      const job = readJob(accountId);
      if (!job || job.status !== JOB_STATUS.READY || !job.result) return false;

      const { outcome, message, rebalanceApiData } = job.result;

      // Restore the request-time account + rebalance context so the preview /
      // modal reflects (and submits against) the correct account.
      restoreRebalanceContext({ accountId: job.accountId ?? accountId, ...(job.snapshot || {}) });

      if (isPreviewableOutcome(outcome) && rebalanceApiData) {
        loadReadyResultIntoPreview(rebalanceApiData);
        clearRebalanceJob(accountId);
        // Push the workbench FIRST so that browser-back from Order Preview lands
        // on the workbench (not the arbitrary page the notification was opened
        // from). React batches both, so the workbench never actually renders.
        navigate("/portfolio-rebalance");
        navigate("/portfolio-rebalance/order-preview");
      } else {
        setPendingInfoModal({
          title: outcome === REBALANCE_OUTCOME.ERROR ? "Error" : undefined,
          message: message || "Rebalance completed.",
        });
        clearRebalanceJob(accountId);
        navigate("/portfolio-rebalance");
      }
      return true;
    },
    [navigate, loadReadyResultIntoPreview, restoreRebalanceContext, setPendingInfoModal, clearRebalanceJob]
  );
};

/**
 * Opener for the snackbar and the workbench mount. If ANY account is ready it
 * routes to the workbench — it NEVER auto-opens the Order Preview, not even for
 * a single ready account. The user then opens a specific account from the
 * workbench via its inline "View Results" (guided by the dropdown dots).
 * Returns the number of ready results.
 *
 * `preferredAccountId` (optional): when the caller points at a single ready
 * account — a single-account notification, where the snackbar named it — the
 * workbench opens FOCUSED on that account (it is selected, so its inline
 * "View Results" affordance and the correct header context show immediately).
 * We still do NOT auto-open the Order Preview (that stays a deliberate click).
 * With no preferred account (workbench mount, nav dot, or a multi-account
 * notification) we just route to the workbench and let the user pick via the
 * green dropdown dots — unchanged behavior.
 *
 * (The fast <60s path is separate: the header's loader presents the result
 * directly via useOpenRebalanceResult — this hook is only the notification path.)
 */
export const useOpenReadyResults = () => {
  const navigate = useNavigate();
  const setSelectedAccount = usePortfolioRebalanceStore((s) => s.setSelectedAccount);
  return useCallback(
    (preferredAccountId = null) => {
      const ready = listReadyUnseenJobs();
      if (ready.length >= 1) {
        // Only select when the requested account is genuinely one of the ready
        // ones (guards against a stale id). We pass the id exactly as it was
        // stored on the job (job.accountId === the original account.id), so it
        // matches the strict === the account dropdown uses to resolve selection.
        const shouldSelect =
          preferredAccountId != null &&
          ready.some(
            ({ accountId }) => String(accountId) === String(preferredAccountId)
          );
        if (shouldSelect) {
          setSelectedAccount(preferredAccountId);
        }
        navigate("/portfolio-rebalance");
      }
      return ready.length;
    },
    [navigate, setSelectedAccount]
  );
};

/**
 * Global watcher for the async rebalance flow. Mounted once (in Layout) so it is
 * active on every authenticated route. It is the SINGLE processor for results —
 * WebSocket push and status poll funnel through processResult(), which correlates
 * a delivery to its account by requestId and dedupes so it's handled exactly once.
 */
const PortfolioRebalanceJobWatcher = () => {
  const { token, isLoggedIn } = useAuth();
  const openReadyResults = useOpenReadyResults();
  const syncRebalanceJobs = usePortfolioRebalanceStore((s) => s.syncRebalanceJobs);
  const rebalanceJobs = usePortfolioRebalanceStore((s) => s.rebalanceJobs);
  const location = useLocation();
  const selectedAccount = usePortfolioRebalanceStore((s) => s.selectedAccount);
  const cancelRebalance = usePortfolioRebalanceStore((s) => s.cancelRebalance);
  // NOT CURRENTLY USED — kept for a possible future revisit. Previous approach:
  // while the header's own "Rebalancing..." loader is showing, render the
  // snackbar behind the loader's Backdrop (lower z-index) and strip
  // interaction, so it's visible but not clickable until the loader clears.
  // Superseded below by cancel-and-view (see handleView): clicking "View
  // Results" now cancels the in-flight rebalance instead of being blocked.
  // const rebalanceLoaderActive = usePortfolioRebalanceStore((s) => s.rebalanceLoaderActive);

  // accountIds: the job(s) THIS toast currently represents (captured at the
  // moment it opened) — handleView reads from here, not a fresh recompute,
  // since by click time those jobs are already marked shown-in-this-tab (below).
  const [snackbar, setSnackbar] = useState({ open: false, message: "", accountIds: [] });
  const pollTimerRef = useRef(null);
  // Read the live route + selected account from refs so the snackbar-decision
  // helpers stay stable (they never re-create processResult / the listeners).
  const pathnameRef = useRef(location.pathname);
  const selectedAccountRef = useRef(selectedAccount);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    selectedAccountRef.current = selectedAccount;
  }, [selectedAccount]);

  // Human-friendly account label for the snackbar (req: say WHICH account).
  const accountLabelOf = useCallback((job) => {
    const fromSnapshot = job?.snapshot?.accountSummary?.accountIdentifier;
    if (fromSnapshot) return fromSnapshot;
    const acct = usePortfolioRebalanceStore
      .getState()
      .accounts.find((a) => String(a.id) === String(job?.accountId));
    return acct?.accountIdentifier || `account ${job?.accountId ?? ""}`.trim();
  }, []);

  // READY & unseen jobs that actually warrant the snackbar. Excludes:
  //  - fresh results (< 60s old): the initiating tab redirects on those, so we
  //    NEVER raise a notification for a result that arrived "under a minute"
  //    (holds even after a reload / in a non-initiating tab — see isJobFresh);
  //  - the selected account while on the workbench: the inline "View Results"
  //    affordance already covers it (req 4).
  const notifiableReadyJobs = useCallback(() => {
    const onWorkbench = pathnameRef.current === WORKBENCH_PATH;
    const sel = selectedAccountRef.current;
    return listReadyUnseenJobs().filter(({ accountId, job }) => {
      if (isJobFresh(job)) return false;
      if (onWorkbench && String(sel) === String(accountId)) return false;
      return true;
    });
  }, []);

  // Recompute the snackbar from the current job map: open it (naming the
  // account(s)) if anything is notifiable, otherwise close it.
  //
  // CRITICAL: a job's toast must be shown at most ONCE PER TAB — not "once per
  // mount". Without this, a dismissed/ignored notification came back on every
  // reload (refreshSnackbar also runs on mount) because nothing remembered it
  // had already been surfaced. The marker is `hasToastShownThisTab` (backed by
  // sessionStorage: private to this tab, surviving its reloads) — distinct from
  // `seen` (= actually opened): the dot / inline "View Results" must keep
  // showing an unseen result indefinitely, but the ephemeral toast fires once
  // per tab. Per-tab (not the old shared `notified` flag) is deliberate: every
  // open tab surfaces the toast once on its own instead of the first tab
  // suppressing it in all the others.
  const refreshSnackbar = useCallback(() => {
    const notifiable = notifiableReadyJobs();
    const unnotified = notifiable.filter(({ job }) => !hasToastShownThisTab(job.requestId));
    if (unnotified.length === 0) {
      setSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
      return;
    }
    const message =
      unnotified.length === 1
        ? `Rebalancing complete for ${accountLabelOf(unnotified[0].job)}.`
        : `Rebalancing complete for ${unnotified.length} accounts.`;
    const accountIds = unnotified.map(({ accountId, job }) => job?.accountId ?? accountId);
    unnotified.forEach(({ job }) => markToastShownThisTab(job.requestId));
    setSnackbar({ open: true, message, accountIds });
  }, [notifiableReadyJobs, accountLabelOf]);

  const processResult = useCallback(
    (dto) => {
      const terminal = isTerminalRebalanceResult(dto);
      // Correlate the delivery to an account by requestId. Fallback: if the
      // payload carries no requestId and exactly one job is pending, use it.
      let match = dto?.requestId ? findJobByRequestId(dto.requestId) : null;
      if (!match && !dto?.requestId) {
        const pending = listPendingJobs();
        if (pending.length === 1) match = pending[0];
      }

      console.log("[rebalance-job] processResult", {
        terminal,
        dtoRequestId: dto?.requestId,
        dtoStatus: dto?.status,
        hasTrades: Array.isArray(dto?.trades),
        matchedAccount: match?.accountId,
        jobStatus: match?.job?.status,
      });

      if (!terminal) {
        console.log("[rebalance-job] skip: not terminal yet");
        return;
      }
      if (!match) {
        console.log("[rebalance-job] skip: no matching pending job");
        return;
      }

      const { accountId, job } = match;
      if (job.status === JOB_STATUS.READY) {
        console.log("[rebalance-job] skip: already READY");
        return;
      }

      const { outcome, message } = classifyRebalanceResult(dto);
      const rebalanceApiData = isPreviewableOutcome(outcome)
        ? { ...buildRebalanceApiData(dto, job.snapshot || {}), _cacheKey: job.cacheKey ?? null }
        : null;

      // If the workbench header is actively awaiting this result (still inside
      // its 60s loader), it presents the result itself → suppress the snackbar.
      const consumed = resolveRebalanceResultWaiter(job.requestId, { outcome, message, accountId });

      writeJob(accountId, {
        ...job,
        status: JOB_STATUS.READY,
        seen: consumed,
        // Drop the (large) positions array — baked into rebalanceApiData — but
        // keep the small summaries so the preview header can be restored later.
        snapshot: {
          portfolioSummary: job.snapshot?.portfolioSummary ?? null,
          accountSummary: job.snapshot?.accountSummary ?? null,
        },
        result: { outcome, message, rebalanceApiData },
      });
      syncRebalanceJobs();
      console.log("[rebalance-job] → READY", { accountId, outcome, consumed });

      // Decide the snackbar from the full job map. A consumed result is marked
      // seen above (the header loader is presenting it → redirect), a fresh
      // result is suppressed (never notify under a minute), and the selected
      // account on the workbench is covered by its inline affordance — all
      // handled inside refreshSnackbar, so this is the single decision point.
      refreshSnackbar();
    },
    [syncRebalanceJobs, refreshSnackbar]
  );

  // Refresh the store mirror from storage on mount (e.g. unseen READY jobs that
  // arrived while this tab was closed / before reload → shows dots) and surface
  // the snackbar for any slow result that is still waiting to be viewed.
  useEffect(() => {
    syncRebalanceJobs();
    refreshSnackbar();
  }, [syncRebalanceJobs, refreshSnackbar]);

  // WebSocket path — dispatched by Requests.jsx on /user/{userId}/rebalance.
  useEffect(() => {
    const handler = (event) => processResult(event.detail);
    window.addEventListener("rebalanceResult", handler);
    return () => window.removeEventListener("rebalanceResult", handler);
  }, [processResult]);

  // Cross-tab sync. The jobs live in localStorage (shared) but each tab has its
  // own in-memory store AND its own in-memory 60s loader waiter. A result is
  // processed by only ONE tab (others hit the READY dedup); the browser then
  // fires `storage` in the OTHER tabs on the localStorage change.
  //
  // Critically, the tab that CLICKED Rebalance may not be the tab that processed
  // the result (the WS is delivered to every tab, and any tab can win the poll).
  // If we did nothing here, that initiating tab's loader would spin until its 60s
  // timeout while the snackbar popped elsewhere — the reported "loader stuck +
  // View Results on top" bug. So on every cross-tab change we resolve any local
  // waiter for a now-ready job: if THIS tab is the initiator, its loader closes
  // and it redirects immediately; if not, resolve is a harmless no-op.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== null && event.key !== PENDING_JOB_KEY) return;
      syncRebalanceJobs();
      for (const { job } of listReadyUnseenJobs()) {
        resolveRebalanceResultWaiter(job.requestId, {
          outcome: job.result?.outcome,
          message: job.result?.message,
          accountId: job.accountId,
        });
      }
      refreshSnackbar();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncRebalanceJobs, refreshSnackbar]);

  // Poll path (backup) — while ANY account has a PENDING job, poll each every
  // 30s (plus an immediate pass). Recovers results missed while away / reloaded,
  // and expires stale jobs.
  const pendingSignature = Object.keys(rebalanceJobs)
    .filter((a) => rebalanceJobs[a]?.status === JOB_STATUS.PENDING)
    .sort()
    .join(",");
  useEffect(() => {
    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    if (!isLoggedIn || listPendingJobs().length === 0) {
      clearPoll();
      return;
    }

    const poll = async () => {
      const pending = listPendingJobs();
      if (pending.length === 0) {
        clearPoll();
        return;
      }
      const activeToken = token || localStorage.getItem("token");
      for (const { accountId, job } of pending) {
        try {
          const dto = await fetchRebalanceStatus(activeToken, job.requestId);
          console.log("[rebalance-job] poll →", {
            accountId,
            requestId: job.requestId,
            dtoStatus: dto?.status,
            terminal: isTerminalRebalanceResult(dto),
          });
          if (dto && isTerminalRebalanceResult(dto)) {
            processResult(dto);
            continue;
          }
        } catch (error) {
          console.warn("[rebalance-job] poll failed", error);
        }
        // No result yet — expire if it has been pending too long (lambda/backend
        // likely failed). Synthesize an ERROR so the notify path runs and the
        // job leaves PENDING (unblocking that account + stopping its poll).
        const latest = readJob(accountId);
        if (isJobStale(latest)) {
          processResult({
            requestId: latest.requestId,
            success: false,
            message: STALE_JOB_MESSAGE,
          });
        }
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return clearPoll;
    // Re-evaluate when the set of pending jobs changes (start/stop polling).
  }, [isLoggedIn, token, pendingSignature, processResult]);

  const handleView = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    // Focus the workbench on the account the notification is about when it names
    // a single one (matches the snackbar message). For a multi-account
    // notification we don't force a selection — the user picks via the green
    // dropdown dots. Read from the toast's own captured accountIds (NOT a
    // fresh notifiableReadyJobs() recompute) — by click time those jobs are
    // already marked shown-in-this-tab, so a live recompute would wrongly come
    // back empty.
    const preferredAccountId = snackbar.accountIds.length === 1 ? snackbar.accountIds[0] : null;

    // Only cancel the selected account's OWN job if it's still within its
    // <60s window (isJobFresh) — that's the only case where the abrupt-
    // redirect race can happen (the header's loader promise could still
    // resolve and force-navigate at any moment). Once a job is past 60s
    // (the "taking longer" / Cancel-link state), there's no redirect risk
    // left, so leave it running — it's a deliberate long-running rebalance
    // the user left going, not something to silently kill just because they
    // clicked a different account's notification.
    const selectedJob = selectedAccount != null ? readJob(selectedAccount) : null;
    if (selectedJob?.status === JOB_STATUS.PENDING && isJobFresh(selectedJob)) {
      cancelRebalance(selectedAccount);
    }

    openReadyResults(preferredAccountId);
  };

  const handleSnackbarClose = (_event, reason) => {
    // Ignore click-away so an errant click elsewhere doesn't dismiss it early;
    // let the 3s timeout ("timeout") and the × button close it.
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Explicit dismiss (× button).
  const handleDismiss = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Snackbar
      open={snackbar.open}
      // Auto-hide after 3s (req). The result stays reachable afterwards via the
      // nav green dot, the account-dropdown dot, and the inline "View Results".
      autoHideDuration={5000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      // NOT CURRENTLY USED — kept for a possible future revisit: sit BEHIND
      // the header's loader Backdrop (drawer+2) while it's up, so the dark
      // overlay dims the snackbar the same way it dims the rest of the
      // screen. Superseded by cancel-and-view (see handleView above).
      // sx={{ zIndex: (theme) => (rebalanceLoaderActive ? theme.zIndex.drawer + 1 : 12000) }}
      sx={{ zIndex: 12000 }}
    >
      {/* Light chip with black text + a primary "View Results" button, kept close
          together. Auto-hides after 3s; the dots + inline link remain. */}
      <SnackbarContent
        sx={{
          backgroundColor: tokens.colors.bodyHoverBackgroundColor,
          color: tokens.colors.fontColour,
          fontWeight: 600,
          borderRadius: "8px",
          border: `1px solid ${tokens.palette.primary}`,
          boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
          "& .MuiSnackbarContent-message": { color: tokens.colors.fontColour },
          // Keep the action close to the message (override MUI's marginLeft:auto).
          "& .MuiSnackbarContent-action": { marginLeft: "8px", paddingLeft: 0 },
          // NOT CURRENTLY USED — see the zIndex comment above; paired
          // view-only treatment for the same (currently disabled) approach.
          // ...(rebalanceLoaderActive && { pointerEvents: "none" }),
        }}
        message={snackbar.message}
        action={
          <>
            <Button
              size="small"
              onClick={handleView}
              // NOT CURRENTLY USED — see the zIndex comment above. Was:
              // disabled={rebalanceLoaderActive}
             sx={{
              // backgroundColor: tokens.palette.primary,
              backgroundColor: "transparent",
              border: `1px solid ${tokens.palette.primary}`,
              color: tokens.palette.primary ,
              fontWeight: 500,
              textTransform: "none",
              borderRadius:  "8px",
              fontSize: "14px",
              px: 1.5,
              "&:hover": { backgroundColor: tokens.palette.primaryA10 },
              // NOT CURRENTLY USED — greyed-out look paired with the disabled
              // prop above; harmless to leave since disabled is never set.
              "&.Mui-disabled": {
                borderColor: tokens.colors.bodyBorderColor50,
                color: tokens.colors.disabledFontColour,
              },
            }}
            >
              View Results
            </Button>
            <IconButton
              size="small"
              aria-label="dismiss"
              onClick={handleDismiss}
              // NOT CURRENTLY USED — see the zIndex comment above. Was:
              // disabled={rebalanceLoaderActive}
              sx={{
                color: tokens.colors.fontColour,
                ml: 0.5,
                "&.Mui-disabled": { color: tokens.colors.disabledFontColour },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </Snackbar>
  );
};

export default PortfolioRebalanceJobWatcher;
